import React, { useState, useEffect } from 'react';
import { notificationAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import { FiBell, FiCheck, FiInfo, FiGift, FiFileText, FiUser, FiSettings, FiTrash2, FiClock, FiAlertTriangle, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await notificationAPI.getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('ไม่สามารถโหลดการแจ้งเตือนได้');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('คุณต้องการลบการแจ้งเตือนนี้ใช่หรือไม่?')) return;
    try {
      await notificationAPI.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('ลบการแจ้งเตือนแล้ว');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('ลบไม่สำเร็จ');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      toast.success('อ่านทั้งหมดแล้ว');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'birthday': return { icon: FiGift, color: 'text-pink-500', bg: 'bg-pink-100' };
      case 'license_expiry': return { icon: FiUser, color: 'text-orange-500', bg: 'bg-orange-100' };
      case 'policy_expiry': return { icon: FiFileText, color: 'text-blue-500', bg: 'bg-blue-100' };
      case 'calendar': return { icon: FiClock, color: 'text-purple-500', bg: 'bg-purple-100' };
      case 'training': return { icon: FiAlertTriangle, color: 'text-green-500', bg: 'bg-green-100' };
      default: return { icon: FiInfo, color: 'text-gray-500', bg: 'bg-gray-100' };
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const renderDetails = (notif) => {
    const { type, data } = notif;
    if (!data || !data.details || !Array.isArray(data.details)) return null;

    return (
      <div className="mt-3 bg-gray-50 p-3 rounded-lg space-y-3 text-sm">
        {data.details.map((item, idx) => (
          <div key={idx} className="border-b border-gray-200 last:border-0 pb-2 last:pb-0">
            {type === 'birthday' && (
              <div className="flex justify-between">
                <div>
                  <span className="font-medium text-gray-900">{item.fullName}</span>
                  {item.nickname && <span className="text-gray-500 ml-1">({item.nickname})</span>}
                </div>
                <div className="text-gray-500">
                  {new Date(item.birthDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}
                </div>
              </div>
            )}
            
            {type === 'license_expiry' && (
              <div>
                <div className="font-medium text-gray-900">{item.fullName}</div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>เลขที่: {item.licenseNo}</span>
                  <span className="text-red-500">หมดอายุ: {new Date(item.licenseExpiryDate).toLocaleDateString('th-TH')}</span>
                </div>
              </div>
            )}

            {type === 'policy_expiry' && (
              <div>
                <div className="font-medium text-gray-900">{item.policyNo}</div>
                <div className="text-xs text-gray-600">ลูกค้า: {item.customerName}</div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>เบี้ย: {Number(item.renewalPremium || item.premium).toLocaleString()}</span>
                  <span className="text-red-500">ครบกำหนด: {new Date(item.renewalDate).toLocaleDateString('th-TH')}</span>
                </div>
              </div>
            )}

            {type === 'calendar' && (
              <div>
                <div className="font-medium text-gray-900">{item.title}</div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{new Date(item.startTime).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} - {new Date(item.endTime).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}</span>
                  <span>{item.type}</span>
                </div>
              </div>
            )}

            {type === 'training' && (
              <div>
                <div className="font-medium text-gray-900">{item.courseName}</div>
                <div className="text-xs text-gray-600">{item.institute}</div>
                <div className="text-xs text-gray-500 mt-1">
                  วันที่: {new Date(item.startDate).toLocaleDateString('th-TH')}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">การแจ้งเตือน (Admin)</h1>
          <p className="text-gray-600">รายการแจ้งเตือนทั้งหมดของคุณ</p>
        </div>
        <div className="flex space-x-3">
          <Link to="/admin/notifications/settings" className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 bg-white rounded border border-gray-200 hover:bg-gray-50 transition-colors">
             <SafeIcon icon={FiSettings} className="w-4 h-4 mr-2"/> ตั้งค่า
          </Link>
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium px-3 py-2"
          >
            อ่านทั้งหมด
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {notifications.length > 0 ? (
          notifications.map((notif) => {
            const style = getIcon(notif.type);
            const isExpanded = expandedId === notif.id;
            const hasDetails = notif.data && notif.data.details && notif.data.details.length > 0;

            return (
              <div 
                key={notif.id} 
                className={`bg-white rounded-lg p-4 shadow-sm border-l-4 ${notif.isRead ? 'border-gray-200 opacity-75' : 'border-indigo-500'} flex items-start`}
              >
                <div className={`p-2 rounded-full ${style.bg} mr-4 flex-shrink-0`}>
                  <SafeIcon icon={style.icon} className={`w-5 h-5 ${style.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <h3 className={`text-sm font-semibold ${notif.isRead ? 'text-gray-700' : 'text-gray-900'}`}>
                      {notif.title}
                    </h3>
                    <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                      {new Date(notif.createdAt).toLocaleDateString('th-TH')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                  
                  {hasDetails && (
                    <div className="mt-2">
                      <button 
                        onClick={() => toggleExpand(notif.id)}
                        className="flex items-center text-xs text-indigo-600 hover:text-indigo-800 font-medium focus:outline-none"
                      >
                        {isExpanded ? (
                          <>
                            <SafeIcon icon={FiChevronUp} className="mr-1 w-3 h-3" />
                            ซ่อนรายละเอียด
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiChevronDown} className="mr-1 w-3 h-3" />
                            ดูรายละเอียด
                          </>
                        )}
                      </button>
                      {isExpanded && renderDetails(notif)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col ml-3 space-y-1">
                  {!notif.isRead && (
                    <button
                      onClick={() => handleMarkAsRead(notif.id)}
                      className="text-indigo-600 hover:text-indigo-800 p-1"
                      title="อ่านแล้ว"
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(notif.id)}
                    className="text-gray-400 hover:text-red-600 p-1"
                    title="ลบ"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-10 bg-white rounded-lg shadow-sm">
            <SafeIcon icon={FiBell} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">ไม่มีการแจ้งเตือน</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotifications;
